/**
 * TagDropdown
 * Dropdown para seleção e gerenciamento de tags.
 * 
 * Princípios: Single Responsibility, Componentização
 */

import React, { RefObject, useId } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Tag } from '../../../../types';

interface TagDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    dropdownRef: RefObject<HTMLDivElement>;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    filteredTags: Tag[];
    onSelectTag: (tagId: string) => void;
    onEditTag: (tag: Tag) => void;
    onDeleteTag: (tag: Tag) => void;
    onCreateNew: () => void;
}

export const TagDropdown: React.FC<TagDropdownProps> = ({
    isOpen,
    onClose,
    dropdownRef,
    searchTerm,
    onSearchChange,
    filteredTags,
    onSelectTag,
    onEditTag,
    onDeleteTag,
    onCreateNew
}) => {
    const searchId = useId();

    if (!isOpen) return null;

    return (
        <div
            className="absolute left-0 z-dropdown mt-1 w-48 rounded-lg border border-border bg-surface py-1 shadow-lg"
            ref={dropdownRef}
        >
            {/* Busca */}
            <div className="px-2 py-1.5 border-b border-subtle">
                <input
                    type="text"
                    id={searchId}
                    name="tag-search"
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Buscar..."
                    aria-label="Buscar tag"
                    autoComplete="off"
                    className="w-full px-2 py-1 text-body-sm border border-subtle rounded focus:ring-1 focus:ring-primary focus:border-primary"
                    autoFocus
                />
            </div>

            {/* Lista de tags */}
            <div className="max-h-36 overflow-y-auto py-1">
                {filteredTags.length === 0 ? (
                    <p className="text-caption text-muted text-center py-2 px-2">
                        {searchTerm ? 'Nenhuma encontrada' : 'Todas selecionadas'}
                    </p>
                ) : (
                    filteredTags.map(tag => (
                        <div
                            key={tag.id}
                            className="group flex items-center justify-between w-full px-2.5 py-1.5 hover:bg-surface-subtle transition-colors cursor-pointer rounded"
                            onClick={() => {
                                onSelectTag(tag.id);
                                onClose();
                            }}
                        >
                            <div className="flex items-center gap-2 overflow-hidden">
                                <span
                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: tag.color }}
                                />
                                <span className="text-body-sm font-medium text-secondary truncate">{tag.name}</span>
                            </div>

                            {/* Ações (só aparecem no hover) */}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEditTag(tag);
                                    }}
                                    className="p-1 text-secondary hover:text-primary hover:bg-surface-subtle rounded transition-colors"
                                    title="Editar"
                                >
                                    <AppIcon name="edit" className="w-3 h-3" />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteTag(tag);
                                    }}
                                    className="p-1 text-secondary hover:text-error hover:bg-surface-subtle rounded transition-colors"
                                    title="Excluir"
                                >
                                    <AppIcon name="trash" className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Botão criar nova tag */}
            <div className="border-t border-subtle px-2 py-1">
                <button
                    onClick={onCreateNew}
                    className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-caption text-primary hover:bg-primary-subtle"
                >
                    <AppIcon name="plus" className="w-3 h-3" />
                    Criar tag
                </button>
            </div>
        </div>
    );
};
